import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import ServiceCard from "../components/ServiceCard";
import CTABand from "../components/CTABand";
import { CONDITIONS, SERVICES } from "../data/site";
import { breadcrumbSchema, serviceSchema } from "../lib/schema";

export default function ConditionDetail() {
  const { slug } = useParams();
  const c = CONDITIONS.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/services" replace />;

  const path = `/conditions/${c.slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: `${c.title} Therapy`, path },
  ];
  const related = SERVICES.filter((s) => c.relatedServices.includes(s.slug));
  const others = CONDITIONS.filter((x) => x.slug !== c.slug);

  return (
    <>
      <SEO
        title={c.metaTitle}
        description={c.metaDescription}
        path={path}
        schema={[breadcrumbSchema(crumbs), serviceSchema({ name: c.h1, description: c.metaDescription, path })]}
      />
      <PageHero crumbs={crumbs} eyebrow="Specialty" title={c.h1.replace(" in New Jersey", " in")} accent="New Jersey" underline body={c.intro} testid="condition-hero">
        <div className="mt-7">
          <Link to="/contact" className="btn btn-primary" data-testid="condition-cta">
            Book a Session <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageHero>

      <section className="section" data-testid="condition-body">
        <div className="container-site grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl md:text-3xl">Signs you might benefit</h2>
            <ul className="mt-6 space-y-3">
              {c.signs.map((s) => (
                <li key={s} className="flex gap-3 text-[15px] text-[var(--ink)]">
                  <Check className="h-5 w-5 text-[var(--sage)] shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl">How we treat it</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.approaches.map((a) => (
                <span key={a} className="chip">{a}</span>
              ))}
            </div>
            <p className="mt-6 text-sm text-body leading-relaxed">
              Sessions are held over secure video for clients located anywhere in New Jersey, with weekday evening and weekend availability.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-sm font-medium text-body mr-1 self-center">Also see:</span>
              {others.map((o) => (
                <Link key={o.slug} to={`/conditions/${o.slug}`} className="chip">{o.title} therapy</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section !pt-0">
        <div className="container-site">
          <h2 className="text-2xl md:text-3xl">Related services</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      <CTABand title={`Get help for ${c.title.toLowerCase()} — online`} />
    </>
  );
}
